'use client';

import type { FocusEventHandler, ReactNode, Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldControl, FieldDescription, FieldError, Field as FieldRoot } from '@savvy-kit/components/field';
import { Popover, PopoverContent } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { Command as CommandPrimitive } from 'cmdk';
import { isNil } from 'es-toolkit';
import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';

const POPOVER_ANIMATE =
  'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 duration-150';

type ComboboxOption = {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
  /**
   * Heading this option is listed under. Options sharing a group render together beneath it, in
   * first-appearance order; ungrouped options render as a flat list, unchanged. cmdk hides (does
   * not unmount) a group whose items all filter out, so a heading never shows above an empty
   * section.
   */
  readonly group?: string;
};

type SearchableComboboxProps = {
  readonly id?: string;
  /**
   * Accessible name for the input. Required for a combobox with no visible `<Label>`: cmdk spreads
   * its own `id` and `aria-labelledby` onto the input last, so an external `<Label htmlFor>` or
   * `aria-label` is overwritten. This fills cmdk's own sr-only label, which its `aria-labelledby`
   * already points at — the only place a name survives.
   */
  readonly label?: string;
  /** Muted helper text below the control, announced through `aria-describedby`. */
  readonly description?: string;
  readonly placeholder?: string;
  readonly emptyMessage?: string;
  readonly options: readonly ComboboxOption[];
  readonly value?: string;
  readonly onValueChange?: (value: string) => void;
  readonly onSearchChange?: (search: string) => void;
  /** Fires when the dropdown opens or closes. Use to defer fetching until the picker is engaged. */
  readonly onOpenChange?: (open: boolean) => void;
  readonly isLoading?: boolean;
  readonly shouldFilter?: boolean;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly className?: string;
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly renderSelectedValue?: (option: ComboboxOption) => ReactNode;
  readonly renderOption?: (option: ComboboxOption) => ReactNode;
  readonly showCaret?: boolean;
  // Draws the selected-state check beside each option. Off removes its gutter, which a list whose
  // options carry their own trailing value reads better without.
  readonly showCheck?: boolean;
  readonly clearable?: boolean;
  readonly popoverClassName?: string;
  /**
   * Extra class names on the popover positioner. Use this (not `popoverClassName`)
   * to override the dropdown's stacking context — e.g. `z-[1400]` when rendering
   * inside a higher-z-index modal, since the positioner owns the page-level
   * z-index while the popup lives inside its `isolate` stacking context.
   */
  readonly popoverPositionerClassName?: string;
  /** Merged with the internal input ref (e.g. react-hook-form `field.ref`). */
  readonly inputRef?: Ref<HTMLInputElement>;
  readonly onInputBlur?: FocusEventHandler<HTMLInputElement>;
};

function SearchableCombobox({
  id,
  label,
  description,
  placeholder = 'Search...',
  emptyMessage = 'No results found.',
  options,
  value: controlledValue,
  onValueChange,
  onSearchChange,
  onOpenChange,
  isLoading = false,
  shouldFilter = true,
  disabled = false,
  required = false,
  className,
  error = false,
  errorMessage,
  renderSelectedValue,
  renderOption,
  showCheck = true,
  showCaret = true,
  clearable = false,
  popoverClassName,
  popoverPositionerClassName,
  inputRef: inputRefProp,
  onInputBlur,
}: SearchableComboboxProps) {
  const localInputRef = useRef<HTMLInputElement>(null);

  const assignInputRef = useCallback(
    (el: HTMLInputElement | null) => {
      localInputRef.current = el;
      if (isNil(inputRefProp)) return;
      if (typeof inputRefProp === 'function') {
        inputRefProp(el);
      } else {
        inputRefProp.current = el;
      }
    },
    [inputRefProp],
  );

  const [open, setOpen] = useState(false);
  // Derived from state rather than wired into each setOpen call site: the dropdown opens from
  // focus, typing, and the caret button, and a caller deferring work until the picker is engaged
  // has to hear about all of them. Skips the mount pass so nothing reports a change that
  // did not happen.
  const reportedOpen = useRef(open);
  useEffect(() => {
    if (reportedOpen.current === open) return;

    reportedOpen.current = open;
    onOpenChange?.(open);
  }, [open, onOpenChange]);
  const [internalValue, setInternalValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const anchorRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === currentValue);
  }, [options, currentValue]);

  // On close: show the selected label. On the open transition with a selection: clear it so
  // cmdk's substring `filter` doesn't collapse the list to that single option. The ref gates the
  // clear to the false→true edge — callers commonly pass a fresh `options` reference each render
  // (Apollo etc.), which would otherwise re-fire this on every render and eat keystrokes.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (!isNil(renderSelectedValue)) return;
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) {
      setInputValue(selectedOption?.label ?? '');
    } else if (!wasOpen && !isNil(selectedOption)) {
      setInputValue('');
    }
  }, [selectedOption, open, renderSelectedValue]);

  const handleSelect = useCallback(
    (selectedValue: string) => {
      if (!isControlled) {
        setInternalValue(selectedValue);
      }
      onValueChange?.(selectedValue);
      setOpen(false);
      setInputValue('');
    },
    [isControlled, onValueChange],
  );

  const handleClear = useCallback(() => {
    if (!isControlled) {
      setInternalValue('');
    }
    onValueChange?.('');
    setInputValue('');
  }, [isControlled, onValueChange]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      if (!open) {
        setOpen(true);
      }

      // If using custom selected value display and user types, clear selection
      if (!isNil(renderSelectedValue) && currentValue !== '' && newValue !== '') {
        handleClear();
      }

      // Notify parent of search changes for async search support
      onSearchChange?.(newValue);
    },
    [open, renderSelectedValue, currentValue, handleClear, onSearchChange],
  );

  const handleFocus = useCallback(() => {
    setOpen(true);
    // Select all text on focus for easy replacement (only if not using custom render)
    if (isNil(renderSelectedValue)) {
      localInputRef.current?.select();
    }
  }, [renderSelectedValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle backspace to clear when input is empty
      if ((e.key === 'Backspace' || e.key === 'Delete') && currentValue !== '' && inputValue === '') {
        e.preventDefault();
        handleClear();
      }
      // Handle escape to close and blur
      if (e.key === 'Escape') {
        setOpen(false);
        setInputValue('');
        localInputRef.current?.blur();
      }
      // Handle tab to close
      if (e.key === 'Tab') {
        setOpen(false);
        setInputValue('');
      }
    },
    [currentValue, inputValue, handleClear],
  );

  // Keyed by heading rather than merged per adjacent run: interleaved groups would otherwise
  // render the same heading twice, under duplicate React keys.
  const groupedOptions = useMemo(() => {
    const byHeading = new Map<string | undefined, ComboboxOption[]>();
    for (const option of options) {
      if (option.disabled === true) continue;
      const existing = byHeading.get(option.group);
      if (isNil(existing)) byHeading.set(option.group, [option]);
      else existing.push(option);
    }
    return [...byHeading].map(([heading, groupOptions]) => ({ heading, groupOptions }));
  }, [options]);

  // Determine if we should show the custom selected value overlay
  const showSelectedOverlay = !isNil(renderSelectedValue) && !isNil(selectedOption) && inputValue === '';

  const showClearButton = clearable && currentValue !== '' && !open;

  let overlayPaddingRight = 'pr-3';
  if (showCaret && showClearButton) {
    overlayPaddingRight = 'pr-16';
  } else if (showCaret || showClearButton) {
    overlayPaddingRight = 'pr-9';
  }

  return (
    <FieldRoot invalid={error} disabled={disabled} className={className}>
      <Popover
        open={open}
        onOpenChange={(newOpen, details) => {
          if (
            !newOpen &&
            details.reason === 'outside-press' &&
            !isNil(localInputRef.current) &&
            details.event.target instanceof Node &&
            localInputRef.current.contains(details.event.target)
          ) {
            details.cancel();
            return;
          }
          setOpen(newOpen);
        }}
      >
        <CommandPrimitive
          label={label}
          className="relative"
          shouldFilter={shouldFilter}
          filter={
            shouldFilter
              ? (value, search) => {
                  const option = options.find((opt) => opt.value === value);
                  if (isNil(option)) return 0;
                  return option.label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                }
              : undefined
          }
        >
          <div
            ref={anchorRef}
            className={cn(
              'border-light relative flex h-10 items-center gap-2 rounded-md border bg-card px-3 shadow-xs',
              error && 'ring-destructive/20 border-destructive',
              open && 'border-primary ring-border-light ring-2',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <SearchIcon className="size-4 shrink-0 text-secondary" />
            <FieldControl id={id} required={required} disabled={disabled}>
              {(slot) => (
                <CommandPrimitive.Input
                  ref={assignInputRef}
                  id={id}
                  value={inputValue}
                  aria-describedby={slot['aria-describedby']}
                  aria-invalid={error}
                  aria-required={required || undefined}
                  onValueChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={onInputBlur}
                  onKeyDown={handleKeyDown}
                  disabled={disabled}
                  aria-busy={isLoading}
                  placeholder={showSelectedOverlay ? '' : placeholder}
                  className={cn(
                    'placeholder:text-secondary flex h-full w-full bg-transparent text-sm outline-none disabled:cursor-not-allowed',
                    showSelectedOverlay && 'text-transparent caret-transparent',
                  )}
                />
              )}
            </FieldControl>
            {showSelectedOverlay && (
              <div
                className={cn('pointer-events-none absolute inset-0 flex items-center gap-2 pl-9', overlayPaddingRight)}
              >
                {renderSelectedValue(selectedOption)}
              </div>
            )}
            {showClearButton && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                  localInputRef.current?.focus();
                }}
                className="shrink-0 text-secondary hover:text-foreground"
                aria-label="Clear selection"
              >
                <XIcon className="size-3.5" />
              </button>
            )}
            {showCaret && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!open) {
                    setOpen(true);
                  }
                }}
                className="shrink-0 text-secondary"
                aria-label={open ? 'Close dropdown' : 'Open dropdown'}
              >
                <ChevronDownIcon className="size-4" />
              </button>
            )}
          </div>

          <PopoverContent
            anchor={anchorRef}
            align="start"
            initialFocus={false}
            finalFocus={false}
            className={cn('w-[var(--anchor-width)] p-0 overflow-hidden', POPOVER_ANIMATE, popoverClassName)}
            positionerClassName={popoverPositionerClassName}
          >
            {/* The list stays mounted while loading: cmdk keeps `aria-expanded` and `aria-controls`
                on the input, so swapping it out leaves the input pointing at an absent listbox. */}
            <CommandPrimitive.List className="max-h-[300px] overflow-y-auto overscroll-contain p-1">
              {isLoading ? (
                <CommandPrimitive.Loading label="Loading results" className="flex justify-center items-center py-8">
                  <div className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </CommandPrimitive.Loading>
              ) : (
                <>
                  <CommandPrimitive.Empty className="px-3 py-4 text-center text-sm text-secondary">
                    {/* Announced on change — an advisor who cannot see the popover still has to
                        learn the difference between "no matches" and "the list failed to load". */}
                    <span role="status" aria-live="polite">
                      {emptyMessage}
                    </span>
                  </CommandPrimitive.Empty>
                  {groupedOptions.map(({ heading, groupOptions }) => {
                    const items = groupOptions.map((option) => {
                      const isSelected = option.value === currentValue;
                      return (
                        <CommandPrimitive.Item
                          key={option.value}
                          value={option.value}
                          onSelect={handleSelect}
                          className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm min-w-0 data-[selected=true]:bg-component-hover"
                        >
                          {showCheck ? (
                            <CheckIcon className={cn('size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                          ) : null}
                          <div className="flex-1 min-w-0">
                            {!isNil(renderOption) ? (
                              renderOption(option)
                            ) : (
                              <span className="truncate">{option.label}</span>
                            )}
                          </div>
                        </CommandPrimitive.Item>
                      );
                    });
                    if (isNil(heading)) return items;
                    return (
                      <CommandPrimitive.Group
                        key={heading}
                        heading={heading}
                        className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-secondary"
                      >
                        {items}
                      </CommandPrimitive.Group>
                    );
                  })}
                </>
              )}
            </CommandPrimitive.List>
          </PopoverContent>
        </CommandPrimitive>
      </Popover>

      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldError error={error} errorMessage={errorMessage} />
    </FieldRoot>
  );
}

export type { SearchableComboboxProps, ComboboxOption };
export default SearchableCombobox;
